import { type CONVEYANCE_TYPES, type CheckoutBasket, type Location } from '@koala/sdk';
import { useEffect, useState, useMemo, useRef } from 'react';
import { parseISO } from 'date-fns';
import * as Sentry from '@sentry/nextjs';
import {
  StyledDayTimeSelectContainer,
  StyledLoader,
  StyledNoAvailability,
  StyledTimeSelect,
} from './styles';
import { getHandoffTimes, useHandoffTimes } from './use-handoff-times';
import { getAvailableDays, getTimeslot } from './utils';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import GenericErrorBoundary from '@/components/genericErrorBoundary';
import { Select } from '@/components/uielements/forms/select';
import { useSelector } from '@/redux';
import { getSanitizedOffset, getFormattedDateInTimezone, isDateBefore } from '@/utils/dates';
import { getStoreTimeInfo, isStoreOpenOnDay } from '@/utils/locations';
import Label from '@/components/uielements/label';
import { StyledText } from '@/components/checkout/uielements/styles';

interface Props {
  /** Whether or not the handoff time is currently being updated. */
  isSubmitting: boolean;
  /** Callback to fire when the selected handoff time has changed. */
  onChange: ((value: string) => void) | ((value: string) => Promise<void>);
  /** The currently selected handoff time. */
  getTimeslots?: (timeslots: { label: string; value: string }[]) => void;
  earliestAvailableTime?: string | null;
  value?: string;
  checkoutBasket?: CheckoutBasket;
  location: Location;
  handoffType: CONVEYANCE_TYPES;
  supportsAsap: boolean;
  stack?: boolean;
}

export function HandoffTimePicker({
  isSubmitting,
  onChange,
  getTimeslots,
  earliestAvailableTime,
  value,
  checkoutBasket,
  location,
  handoffType,
  supportsAsap,
  stack = true,
}: Props) {
  const strings = useSelector((state) => state.app.cmsConfig.strings);

  const { storeTimezone, hasRemainingHoursToday, storeDate } = getStoreTimeInfo(
    location,
    handoffType as string,
  );

  const dayNeededForReq = value
    ? getFormattedDateInTimezone(value, 'yyyy-MM-dd', storeTimezone)
    : '';

  /**
   * Find the initial value for the handoff day based on the basket's current
   * handoff time. This will populate the day selector on first render.
   *
   * - If a value is present (which means a user selected a time previously):
   *  - ensure the value isn't "asap" (a non time specific wanted at time)
   *  - format the day in local time as YYYY-MM-DD. This is important as all wanted at times (both selected and returned from the API)
   *    are in UTC, but are displayed in local time (the day value must be calculated in the same timezone as the user)
   * - Otherwise, grab todays date as YYYY-MM-DD in local time.
   */
  const [selectedDay, setSelectedDay] = useState(dayNeededForReq);

  // Retrieve available handoff times for the current order.
  const { data, isLoading, isError } = useHandoffTimes({
    basketId: checkoutBasket?.id,
    dayWanted: selectedDay,
    handoffType,
    locationId: location.id,
    supportsAsap,
  });

  /**
   * Fires when the user selects a different handoff day.
   * It's somewhat complex because we must:
   * a) find available times for the selected day
   * b) set the basket's handoff time to the first available slot on that day
   */
  async function handleDaySelect(day: string) {
    // Optimistically update the dropdown menu.
    setSelectedDay(day);
    // Fetch handoff times for the new day.
    const res = await getHandoffTimes({
      basketId: checkoutBasket?.id,
      dayWanted: day,
      handoffType,
      locationId: location.id,
      supportsAsap,
    });
    // If times are available, set the basket's handoff to the first slot.
    if (res.length > 0) {
      onChange(res[0]);
    } else {
      // If no times are available, reset the basket's handoff time.
      const userGMTOffset = getSanitizedOffset(-(new Date().getTimezoneOffset() / 60));
      onChange(day ? `${day}T00:00:00${userGMTOffset}` : '');
    }
  }

  // Get a list of days that the order can be scheduled for.
  const availableDays = getAvailableDays({
    canOrderAhead: location.order_ahead,
    advanceDays: location.order_ahead_days,
  });
  if (!hasRemainingHoursToday) {
    availableDays.shift();
  }

  //`disable` any day the brand is closed for business
  const days = availableDays.map((day) => {
    return isStoreOpenOnDay(day, location)
      ? { ...day, disabled: false }
      : { ...day, disabled: true };
  });

  useEffect(() => {
    if (
      (!hasRemainingHoursToday && selectedDay === storeDate) ||
      (!selectedDay && days.length > 0)
    ) {
      setSelectedDay(days.find((day) => !day.disabled)?.value ?? '');
    }
  }, [hasRemainingHoursToday, days]);

  // Format the list of handoff times for a given day.
  const timeslots = useMemo(
    () =>
      data?.map(getTimeslot(strings, checkoutBasket, storeTimezone, earliestAvailableTime)) ?? [],
    [data, strings, checkoutBasket, storeTimezone, earliestAvailableTime],
  );

  const initialSetFirstTimeslot = useRef(false);
  useEffect(() => {
    if (
      timeslots.length > 0 &&
      ((!initialSetFirstTimeslot.current && !value) ||
        (!window.location.pathname.includes('checkout') &&
          value &&
          isDateBefore(parseISO(value), parseISO(timeslots[0]?.value))))
    ) {
      initialSetFirstTimeslot.current = true;
      Sentry.addBreadcrumb({
        category: 'time picker',
        message: `Debug time picker onChange event`,
        level: 'debug',
        data: {
          value,
          timeslot: timeslots[0]?.value,
          handoffType,
        },
      });
      onChange(timeslots[0]?.value);
    }
  }, [timeslots, onChange, value, timeslots.length, handoffType]);

  // only handle getTimeslots callback
  useEffect(() => {
    if (getTimeslots && timeslots.length >= 0) {
      getTimeslots(timeslots);
    }
  }, [getTimeslots, timeslots]);

  return (
    <GenericErrorBoundary>
      <StyledDayTimeSelectContainer $stack={stack}>
        <div>
          <Label visuallyHidden={false} htmlFor="day_wanted">
            <StyledText style={{ display: 'block', fontWeight: '600', marginBottom: '10px' }}>
              Change {handoffType} Date:
            </StyledText>
          </Label>
          <Select
            name="day_wanted"
            placeholder="Date Wanted"
            onChange={handleDaySelect}
            includeLabel={false}
            value={selectedDay}
            options={days}
            title="Date Wanted"
          />
        </div>
        {(!data || data.length !== 0) && (
          <StyledTimeSelect $showLoading={isLoading || isSubmitting} $showError={isError}>
            <Label visuallyHidden={false} htmlFor="time_wanted">
              <StyledText style={{ display: 'block', fontWeight: '600', marginBottom: '10px' }}>
                Schedule for a Later {handoffType} Time:
              </StyledText>
            </Label>
            <Select
              name="time_wanted"
              placeholder="Time Wanted Mode"
              onChange={onChange}
              includeLabel={false}
              // value could be empty string so we set it to first available timeslot
              // it works along with useEffect above
              value={value ?? timeslots?.[0]?.value}
              options={timeslots}
              title="Time Wanted"
              errorMessage={isError ? 'Please select a different time.' : undefined}
            />
            {(isLoading || isSubmitting) && <StyledLoader />}
          </StyledTimeSelect>
        )}
        {data && data.length === 0 && (
          <StringAccessor
            tag={StyledNoAvailability}
            accessor="handoff_time.no_available_times"
            html={true}
          />
        )}
      </StyledDayTimeSelectContainer>
    </GenericErrorBoundary>
  );
}
